import { useId, useState } from 'react';
import { strings } from '@/shared/strings';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { modalStyles } from '@/ui/primitives/Modal';

export interface BulkReasonDialogProps {
  title: string;
  /** What will happen, as a plain question saying how many: "Archive 3 cases?". */
  statement: string;
  lead: string;
  reasonLabel: string;
  placeholder: string;
  maxLength: number;
  /** A deletion needs one; an archive does not, since most are finished work. */
  required: boolean;
  /** Said when a required reason is left empty, in the API's words. */
  requiredMessage?: string;
  confirmLabel: string;
  busy: boolean;
  /** A refusal of the whole request, shown where the choice was made. */
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * One reason for a whole selection, to archive it or to delete it. Section 4 of
 * the request of 24 September 2026. The single deletion's dialog in shape and
 * words, so deleting twenty cases reads as deleting one does.
 *
 * Since the request of 5 October 2026 (third), section 2, the dialog is the
 * confirmation and not a form in front of one: built on the shared
 * `ConfirmSheet`, with the question above the reason.
 */
export function BulkReasonDialog({
  title,
  statement,
  lead,
  reasonLabel,
  placeholder,
  maxLength,
  required,
  requiredMessage,
  confirmLabel,
  busy,
  error,
  onCancel,
  onConfirm,
}: BulkReasonDialogProps) {
  const reasonId = useId();
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);

  function confirm(): boolean {
    if (required && reason.trim() === '') {
      setMissing(true);
      return false;
    }
    onConfirm(reason.trim());
    return true;
  }

  const shown = missing ? (requiredMessage ?? null) : error;

  return (
    <ConfirmSheet
      title={title}
      statement={statement}
      confirmLabel={confirmLabel}
      cancelLabel={strings.bulk.cancel}
      closeLabel={strings.sheet.close}
      onConfirm={confirm}
      onCancel={onCancel}
      busy={busy}
    >
      <div className={modalStyles.field}>
        <span className={modalStyles.hint}>{lead}</span>
      </div>

      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor={reasonId}>
          {reasonLabel}
        </label>
        <textarea
          id={reasonId}
          className={modalStyles.textarea}
          value={reason}
          maxLength={maxLength}
          placeholder={placeholder}
          data-autofocus
          onChange={(event) => {
            setReason(event.target.value);
            setMissing(false);
          }}
        />
      </div>

      {shown !== null && (
        <p className={modalStyles.error} role="alert">
          {shown}
        </p>
      )}
    </ConfirmSheet>
  );
}
